import React, { useState } from 'react';
import { FaQuestionCircle, FaSearch, FaEnvelope, FaPhone, FaBook, FaVideo, FaFileAlt, FaArrowRight, FaUserTie } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';

function HelpPage({ onLogout }) {
  const [searchTerm, setSearchTerm] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const helpTopics = [
    {
      title: 'Getting Started',
      icon: FaBook,
      items: [
        'How to log in to the system',
        'Understanding the dashboard',
        'Setting up your profile',
        'Navigating the sidebar'
      ]
    },
    {
      title: 'Managing Jobs',
      icon: FaFileAlt,
      items: [
        'Creating a new job',
        'Assigning subcontractors to jobs',
        'Tracking job progress',
        'Marking jobs as complete'
      ]
    },
    {
      title: 'Clients & Subcontractors',
      icon: FaQuestionCircle,
      items: [
        'Adding a new client',
        'Adding a new subcontractor',
        'Managing client documents',
        'Viewing subcontractor details'
      ]
    },
    {
      title: 'Invoices & Payments',
      icon: FaFileAlt,
      items: [
        'Creating an invoice',
        'Sending invoices to clients',
        'Marking invoices as paid',
        'Managing payment terms'
      ]
    },
    {
      title: 'Reports & Analytics',
      icon: FaFileAlt,
      items: [
        'Generating tonnage reports',
        'Viewing revenue reports',
        'Exporting reports to PDF/Excel',
        'Filtering reports by date'
      ]
    },
    {
      title: 'Notifications & Messages',
      icon: FaQuestionCircle,
      items: [
        'Understanding notifications',
        'Sending messages to clients/subbies',
        'Managing unread messages',
        'Viewing notification history'
      ]
    }
  ];

  const filteredTopics = helpTopics.filter(topic =>
    topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    topic.items.some(item => item.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout activeTab="help" onLogout={onLogout} user={user}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-navy mb-2">🆘 Help & Support</h1>
        <p className="text-gray-500 mb-6">Find answers to common questions and learn how to use the system</p>

        {/* Search */}
        <div className="relative mb-8">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search help topics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-green"
          />
        </div>

        {/* Guides — full articles (linked pages) */}
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-1">Guides</h2>
          <p className="text-sm text-gray-500 mb-3">In-depth explanations you can read, print or send on.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/help/accounting"
              className="group bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                  <FaBook size={18} />
                </div>
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-50 text-green-700">Guide</span>
              </div>
              <h3 className="font-semibold text-navy mt-3 group-hover:text-green-600 transition-colors">Accounting &amp; QuickBooks Guide</h3>
              <p className="text-sm text-gray-500 mt-1">
                How the money side works: what syncs to QuickBooks, what does not, the 5-step push procedure, what your accountant still does manually, and troubleshooting from the Sync Log.
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-400 flex items-center gap-1"><FaUserTie size={11} /> For finance &amp; your accountant</span>
                <span className="text-xs text-green-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <FaArrowRight size={10} />
                </span>
              </div>
            </Link>
          </div>
        </div>

        {/* Quick Contact */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaEnvelope className="text-blue-600" size={20} />
            </div>
            <h4 className="font-semibold text-navy">Email Support</h4>
            <p className="text-sm text-gray-500">support@onepurposelogistics.com</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaPhone className="text-green-600" size={20} />
            </div>
            <h4 className="font-semibold text-navy">Phone Support</h4>
            <p className="text-sm text-gray-500">+27 63 598 5855</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaVideo className="text-purple-600" size={20} />
            </div>
            <h4 className="font-semibold text-navy">Video Tutorials</h4>
            <p className="text-sm text-gray-500">Coming soon</p>
          </div>
        </div>

        {/* Help Topics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTopics.map((topic, index) => {
            const Icon = topic.icon;
            return (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-semibold text-navy">{topic.title}</h3>
                </div>
                <ul className="space-y-2">
                  {topic.items.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-600 hover:text-green-600 cursor-pointer flex items-center gap-2">
                      <span className="w-1 h-1 bg-green-400 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Need more help? Contact your system administrator or email support@onepurposelogistics.com</p>
        </div>
      </div>
    </AdminLayout>
  );
}

export default HelpPage;
